import { add, dist, scale, type Vec3 } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, setHv } from "../../sim/components.ts";
import { aim, applyDamage, applyHeal, applyStatus, isEnemy, playersInRadius, rayAnyPlayer, spawnZone } from "../../sim/combat.ts";
import { canTargetConstruct, constructsInRadius, damageConstruct, nearestEnemy, rayConstructs, spawnConstruct } from "../../sim/constructs.ts";
import { FxKind } from "../../sim/events.ts";
import { disguise } from "../../sim/sim.ts";
import { targetPoint } from "../../sim/targeting.ts";
import { HERO_COUNT, HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { ck, fxk, zk, type HeroModule } from "../module.ts";
import { action, aura, commonInput, constructAimAt, constructHitscan, consume, mimicOwner, ready, secs, stealth, trigger, useUlt } from "../kitlib.ts";

export const V = { stealth: 0, cloneCount: 1, borrowedHero: 2, ult: 3 } as const;
export const CK = { Double: ck(HeroId.Lowkey, 0), Decoy: ck(HeroId.Lowkey, 1) } as const;
export const ZK = { Rune: zk(HeroId.Lowkey, 0) } as const;
export const FX = { Trick: fxk(HeroId.Lowkey, 0) } as const;

const def: HeroDef = {
  id: HeroId.Lowkey,
  slug: "lowkey",
  name: "Lowkey",
  role: Role.Support,
  color: "#3fae5c",
  blurb: "A trickster support who multiplies his fire through illusions and swaps places with decoys.",
  health: 275,
  armor: 0,
  shields: 0,
  radius: 0.42,
  height: 1.86,
  headRadius: 0.23,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 10,
  reloadTime: 1.5,
  ultCost: 4500,
  abilities: {
    [Slot.Primary]: { name: "Forked Scepter", key: "LMB", description: "Fire a mystic bolt that damages enemies or heals allies.", cooldown: 0 },
    [Slot.Secondary]: { name: "Curtain Trick", key: "RMB", description: "Leave a decoy and enter stealth while you reposition.", cooldown: 15 },
    [Slot.Ability1]: { name: "Mercy Rune", key: "Shift", description: "Drop runes at you and your illusions, healing and protecting allies.", cooldown: 30 },
    [Slot.Ability2]: { name: "Double Take", key: "E", description: "Aim, then LMB to create an illusion. It copies your attacks.", cooldown: 12, charges: 2 },
    [Slot.Ability3]: { name: "Switcheroo", key: "F", description: "Swap positions with the nearest illusion.", cooldown: 15 },
    [Slot.Ultimate]: { name: "Borrowed Crown", key: "Q", description: "Become the last enemy hero your tricks copied for 12 seconds.", cooldown: 0 },
  },
  passive: { name: "Knife Smile", description: "Quick melee hits harder from behind." },
};

function activeClones(sim: Parameters<HeroModule["kit"]["tick"]>[0], owner: number) {
  return sim.constructEids().filter((e) => sim.c.Construct.owner[e] === owner && (sim.c.Construct.kind[e] === CK.Double || sim.c.Construct.kind[e] === CK.Decoy));
}

function placeClone(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, kind: number, pos: Vec3) {
  if (activeClones(sim, eid).length >= 2) {
    const oldest = activeClones(sim, eid)[0];
    if (oldest !== undefined) sim.destroyEntity(oldest);
  }
  spawnConstruct(sim, eid, kind, pos, sim.c.Look.yaw[eid]!, { seconds: kind === CK.Decoy ? 8 : 60 });
}

function rememberCopiedEnemy(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, from: Vec3) {
  const copied = nearestEnemy(sim, eid, add(from, { x: 0, y: 1, z: 0 }), 35, { los: true });
  if (copied && !copied.construct) setHv(sim.c, eid, V.borrowedHero, sim.c.Player.hero[copied.eid]! + 1);
}

function borrowedTarget(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number): HeroId | undefined {
  const remembered = Math.floor(hv(sim.c, eid, V.borrowedHero)) - 1;
  if (remembered >= 0 && remembered < HERO_COUNT) return remembered as HeroId;
  let best: { eid: number; d: number } | undefined;
  const from = sim.posOf(eid);
  for (const other of sim.playerEids()) {
    if (other === eid || !sim.c.Player.alive[other] || !isEnemy(sim, eid, other)) continue;
    const d = dist(from, sim.posOf(other));
    if (!best || d < best.d) best = { eid: other, d };
  }
  if (!best) return undefined;
  return sim.c.Player.hero[best.eid] as HeroId;
}

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      for (const v of Object.values(V)) setHv(sim.c, eid, v, 0);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      const clones = activeClones(sim, eid);
      setHv(c, eid, V.cloneCount, clones.length);
      setHv(c, eid, V.stealth, c.Status.stealth[eid]! > 0 ? 1 : 0);
      rememberCopiedEnemy(sim, eid, sim.posOf(eid));
      for (const cl of clones) rememberCopiedEnemy(sim, eid, sim.posOf(cl));
      for (const z of sim.zoneEids()) if (c.Zone.owner[z] === eid && c.Zone.kind[z] === ZK.Rune) rememberCopiedEnemy(sim, eid, sim.posOf(z));
      setHv(c, eid, V.ult, 0);
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;
      const ultTarget = (pressed & Btn.Ultimate) ? borrowedTarget(sim, eid) : undefined;
      if ((pressed & Btn.Ultimate) && ultTarget === undefined) return;
      if (useUlt(sim, eid, input, pressed)) {
        sim.emit({ type: "impact", pos: add(sim.posOf(eid), { x: 0, y: 1, z: 0 }), kind: FX.Trick });
        disguise(sim, eid, ultTarget!, 12, { keepUlt: true, hpFrac: true });
        return;
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 12);
        const p = targetPoint(sim, eid, Slot.Ability2);
        placeClone(sim, eid, CK.Double, p.pos);
      }
      if (pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        action(sim, eid, ActionKind.Cast, 12);
        placeClone(sim, eid, CK.Decoy, sim.posOf(eid));
        stealth(sim, eid, 4, { source: eid });
        applyHeal(sim, eid, eid, 30, { noUlt: true });
      }
      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        action(sim, eid, ActionKind.Cast, 14);
        spawnZone(sim, eid, ZK.Rune, sim.posOf(eid), 5, 5);
        for (const cl of clones) spawnZone(sim, eid, ZK.Rune, sim.posOf(cl), 5, 5);
      }
      if (pressed & Btn.Ability3 && ready(sim, eid, Slot.Ability3)) {
        const near = constructsInRadius(sim, sim.posOf(eid), 30, (e) => c.Construct.owner[e] === eid && (c.Construct.kind[e] === CK.Double || c.Construct.kind[e] === CK.Decoy))[0];
        if (near) {
          consume(sim, eid, Slot.Ability3);
          const here = sim.posOf(eid);
          const there = sim.posOf(near.eid);
          c.Pos.x[eid] = there.x;
          c.Pos.y[eid] = there.y;
          c.Pos.z[eid] = there.z;
          c.Pos.x[near.eid] = here.x;
          c.Pos.y[near.eid] = here.y;
          c.Pos.z[near.eid] = here.z;
          applyStatus(sim, eid, "phased", 0.2, { source: eid });
          sim.emit({ type: "impact", pos: there, kind: FX.Trick });
        }
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
        c.Weapon.fireCd[eid] = secs(1 / 1.75);
        action(sim, eid, ActionKind.Fire, 9);
        const a = aim(sim, eid);
        const hit = rayAnyPlayer(sim, eid, a.origin, a.dir, 35);
        const con = rayConstructs(sim, a.origin, a.dir, hit ? hit.t : 35, (e) => canTargetConstruct(sim, e, eid));
        const end = con ? con.pos : hit ? add(a.origin, scale(a.dir, hit.t)) : a.point;
        if (con) damageConstruct(sim, con.eid, 25, eid);
        else if (hit) {
          if (isEnemy(sim, eid, hit.target)) applyDamage(sim, hit.target, 25, eid, { pos: end });
          else applyHeal(sim, eid, hit.target, 55);
        }
        sim.emit({ type: "tracer", owner: c.Net.id[eid]!, from: a.origin, to: end, kind: FxKind.Spore });
      }
    },
  },
  targets: {
    [Slot.Ability2]: { range: 30, clearance: 0.45, construct: CK.Double, verb: "Place" },
  },
  constructs: {
    [CK.Double]: {
      maxHp: 250,
      seconds: 60,
      shape: { kind: "cyl", r: 0.42, h: 1.86 },
      damageable: true,
      visual: { color: "#3fae5c", shape: "cyl" },
      tick(sim, e) {
        const owner = sim.c.Construct.owner[e]!;
        if (!sim.players.has(owner)) return;
        mimicOwner(sim, owner, e);
        const act = sim.c.Weapon.action[owner]!;
        if (act === ActionKind.Fire && sim.c.Weapon.actionTicks[owner]! === 9) {
          const h = 1.2;
          constructHitscan(sim, e, constructAimAt(sim, e, aim(sim, owner).point, h), { range: 35, damage: 20, fxKind: FxKind.Spore, tracer: true, height: h });
        }
      },
    },
    [CK.Decoy]: {
      maxHp: 250,
      seconds: 8,
      shape: { kind: "cyl", r: 0.42, h: 1.86 },
      damageable: true,
      visual: { color: "#f5d76e", shape: "cyl" },
      tick(sim, e) {
        const owner = sim.c.Construct.owner[e]!;
        if (sim.players.has(owner)) mimicOwner(sim, owner, e);
      },
    },
  },
  zones: {
    [ZK.Rune]: {
      visual: { color: "#3fae5c", dome: true },
      tick(sim, z) {
        const owner = sim.c.Zone.owner[z]!;
        aura(sim, owner, sim.posOf(z), { radius: sim.c.Zone.radius[z]!, healAlliesPerSec: 100, los: false });
        for (const { eid } of playersInRadius(sim, sim.posOf(z), sim.c.Zone.radius[z]!, (e) => !isEnemy(sim, owner, e), false)) applyStatus(sim, eid, "resist", 0.2, { source: owner, resistPct: 0.3 });
      },
    },
  },
  fx: { [FX.Trick]: { color: "#3fae5c" } },
};

export default module;
